import React, { useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";
import "./Rosario.scss";

const Coronilla = () => {
  const { t } = useTranslation();

  // Estado: 0 = inicio, 1-58 = oraciones, 59+ = completado
  const [step, setStep] = useState(0);

  // Estructura: 3 iniciales + (5 décadas × 11) = 58 oraciones
  const totalSteps = 58;

  // Calcular década actual (0-4)
  const currentDecade = step > 3 ? Math.floor((step - 4) / 11) : -1;

  // Obtener tipo de oración actual
  const getPrayerType = useCallback(() => {
    if (step === 0) return "intro";
    if (step === 1) return "our_father";
    if (step === 2) return "hail_mary";
    if (step === 3) return "apostles_creed";
    if (step >= totalSteps - 2) return "holy_god_final";
    if (step > 3) {
      const pos = (step - 4) % 11;
      if (pos < 10) return "for_his_passion";
      return "holy_god";
    }
    return "";
  }, [step]);

  // Obtener título corto
  const getPrayerTitle = () => {
    const type = getPrayerType();
    if (!type) return "";

    if (type === "for_his_passion") {
      const beadInDecade = (step - 4) % 11;
      return `${t(`coronilla.bead.${type}`)} ${beadInDecade + 1}/10`;
    }

    if (type === "holy_god_final") {
      const finalNum = step - (totalSteps - 3);
      return `${t(`coronilla.bead.holy_god`)} ${finalNum}/3`;
    }

    return t(`coronilla.bead.${type}`);
  };

  const prayerType = getPrayerType();

  // Handlers
  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleReset = () => {
    setStep(0);
  };

  // ===== VISTA DE INICIO =====
  if (step === 0) {
    return (
      <div className="rosario-simple">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="mb-3">
                <h1 className="coronilla-title mb-2">{t("coronilla.title")}</h1>
                <p className="text-muted lead">{t("coronilla.subtitle")}</p>
              </div>

              <Card className="text-center mystery-preview mb-4">
                <Card.Body>
                  <div className="mystery-icon-large">✝️</div>
                  <h3 className="mt-3">{t("coronilla.jesus_mercy")}</h3>
                  <p className="text-muted mt-3">
                    {t("coronilla.description")}
                  </p>
                </Card.Body>
              </Card>

              <div className="text-center mb-4">
                <p className="text-muted">
                  <small>📖 {t("coronilla.origin")}</small>
                </p>
                <p className="text-muted">
                  <small>{t("coronilla.origin_details")}</small>
                </p>
              </div>

              <Card className="instructions-card mb-4">
                <Card.Body>
                  <p className="mb-0">{t("coronilla.instructions")}</p>
                </Card.Body>
              </Card>

              <Button
                variant="primary"
                size="lg"
                className="w-100 py-3 start-button"
                onClick={handleNext}
              >
                {t("coronilla.start")} →
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE COMPLETADO =====
  if (step >= totalSteps) {
    return (
      <div className="rosario-simple completion">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="completion-icon">❤️</div>
              <h1 className="mt-4">{t("coronilla.completed.title")}</h1>
              <p className="lead">{t("coronilla.completed.message")}</p>
              <p className="text-muted mb-4">{t("coronilla.completed.blessing")}</p>

              <Card className="completion-prayer mb-4">
                <Card.Body>
                  <p>{t("coronilla.prayers.final_prayer")}</p>
                </Card.Body>
              </Card>

              <div className="d-grid gap-2">
                <Button variant="primary" size="lg" onClick={handleReset}>
                  {t("coronilla.actions.pray_again")}
                </Button>
                <Button variant="outline-secondary" href="/">
                  {t("rosary.actions.home")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE ORACIÓN =====
  // Progreso de décadas (5)
  const displayDecade = currentDecade >= 0 ? currentDecade + 1 : 0;

  // Handler para tap con prevención de doble disparo
  const handleCardTap = (e) => {
    if (e.target.closest("button")) {
      return;
    }
    handleNext();
  };

  return (
    <div className="rosario-simple prayer-view">
      <Container className="rosario-container">
        <div className="prayer-content-wrapper">
          {/* Header con indicador de década */}
          <div className="rosario-header mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button variant="link" className="text-muted p-0" onClick={handleReset}>
                ← {t("rosary.actions.back")}
              </Button>
              <div className="decade-counter">
                <span className="decade-current">{displayDecade}</span>
                <span className="decade-separator">/</span>
                <span className="decade-total">5</span>
              </div>
            </div>

            {/* Etiqueta */}
            <div className="mystery-label text-center">
              {t("coronilla.title")}
            </div>
          </div>

          {/* Barra de progreso segmentada por décadas */}
          <div className="decade-progress-container mb-4">
            {Array.from({ length: 5 }, (_, i) => {
              const segmentProgress =
                i < currentDecade
                  ? 100
                  : i > currentDecade
                  ? 0
                  : step > 3
                  ? ((step - 4) % 11) / 11 * 100
                  : 0;
              return (
                <div
                  key={i}
                  className={`decade-segment ${
                    i < currentDecade ? "completed" : ""
                  } ${i === currentDecade ? "active" : ""}`}
                >
                  <div
                    className="decade-segment-fill"
                    style={{ width: `${segmentProgress}%` }}
                  />
                  <span className="decade-number">{i + 1}</span>
                </div>
              );
            })}
          </div>

          {/* Tarjeta de oración principal */}
          <Card className="prayer-card-main" onClick={handleCardTap}>
            <Card.Body className="prayer-card-body">
              <div className="prayer-content">
                {/* Título de la oración */}
                <h2 className="prayer-title-main mb-4">{getPrayerTitle()}</h2>

                {/* Texto de la oración */}
                <p className="prayer-text-main lead">
                  {t(`coronilla.prayers.${prayerType}`)}
                </p>

                {/* Indicador de tap */}
                <div className="tap-indicator mt-4 text-muted">
                  <small>{t("rosary.simple.tap_continue")}</small>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Controles de navegación */}
          <div className="navigation-controls d-flex justify-content-between gap-2 mt-4">
            <Button
              variant="outline-secondary"
              className="nav-btn flex-grow-1"
              onClick={handlePrevious}
              disabled={step === 1}
            >
              ← {t("rosary.simple.previous")}
            </Button>
            <Button
              variant="primary"
              className="nav-btn flex-grow-1"
              onClick={(e) => {
                e.stopPropagation();
                handleNext();
              }}
            >
              {t("rosary.simple.next")} →
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default Coronilla;
